import React from 'react';
import Box from '@mui/material/Box';
import { getAnswerColor, normalizeBetChoices } from '../../betLabels';

export const parseAnswers = (answers) => {
  if (Array.isArray(answers)) return normalizeBetChoices(answers);
  if (typeof answers !== 'string' || answers.trim() === '') return [];

  try {
    const parsed = JSON.parse(answers);
    return normalizeBetChoices(parsed);
  } catch (error) {
    console.error('Error parsing answers JSON', error);
    return [];
  }
};

const formatRate = (rate) => {
  if (rate === null || rate === undefined || rate === '' || rate === '-') {
    return '-';
  }

  const rateValue = typeof rate === 'object' ? (rate.rate ?? rate.optional_rate ?? rate.base_rate) : rate;
  if (rateValue === null || rateValue === undefined || rateValue === '' || rateValue === '-') {
    return '-';
  }

  const numericRate = Number(rateValue);
  return Number.isFinite(numericRate) ? numericRate.toFixed(2) : String(rateValue);
};

export const normalizeRates = (payload, choices = []) => {
  const nextRates = choices.map(() => '-');
  const source = payload?.rates ?? payload;

  const assignRate = (index, value) => {
    if (index < 0 || index >= nextRates.length) {
      return;
    }

    nextRates[index] = formatRate(value);
  };

  if (Array.isArray(source)) {
    source.forEach((value, index) => assignRate(index, value));
    return nextRates;
  }

  if (source && typeof source === 'object') {
    Object.entries(source).forEach(([key, value]) => assignRate(Number(key), value));
  }

  return nextRates;
};

function ReadOnlyBetChoiceCardBase({ choice, index, rate, compact = false }) {
  const colorSet = getAnswerColor(index);

  return (
    <Box
      sx={{
        position: 'relative',
        mb: compact ? 0 : 1,
        fontSize: '13px',
        p: compact ? 1.25 : 1.5,
        borderRadius: '14px',
        border: `1px solid ${colorSet.border}`,
        backgroundColor: colorSet.surface,
        minWidth: 0,
      }}
    >
      <Box
        component="h3"
        sx={{
          fontSize: compact ? '14px' : '15px',
          margin: 0,
          color: colorSet.primary,
          lineHeight: 1.35,
          wordBreak: 'break-word',
        }}
      >
        {choice}
      </Box>
      <Box sx={{ mt: 1, color: colorSet.primary, fontWeight: 700 }}>
        レート: <span style={{ fontWeight: 'bold' }}>{formatRate(rate)}倍</span>
      </Box>
    </Box>
  );
}

export function ReadOnlyBetChoiceCardPC(props) {
  return <ReadOnlyBetChoiceCardBase {...props} compact />;
}

export function ReadOnlyBetChoiceCardSP(props) {
  return <ReadOnlyBetChoiceCardBase {...props} />;
}

export const getPastResultEmptyMessage = (targetNumbers) => (
  Array.isArray(targetNumbers) && targetNumbers.length > 0
    ? '確定済みの過去結果はまだありません'
    : '過去ラウンド結果はまだありません'
);

const getResultIndex = (result) => {
  const resultIndex = Number(result?.result);
  return Number.isInteger(resultIndex) ? resultIndex : 0;
};

const getResultLabel = (result, fallbackChoices = []) => {
  const resultIndex = getResultIndex(result);

  return result?.resultLabel
    || result?.result_label
    || result?.answers?.[resultIndex]
    || fallbackChoices?.[resultIndex]
    || '結果取得済み';
};

function ResultLegend({ choices }) {
  const visibleChoices = normalizeBetChoices(choices);

  if (visibleChoices.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 0.75,
        mt: 1,
      }}
    >
      {visibleChoices.map((choice, index) => {
        const colorSet = getAnswerColor(index);

        return (
          <Box
            key={`${choice}-${index}`}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              minWidth: 0,
              fontSize: '12px',
              fontWeight: 800,
              color: 'rgba(15, 23, 42, 0.72)',
              lineHeight: 1.35,
            }}
          >
            <Box
              component="span"
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: colorSet.primary,
                border: `1px solid ${colorSet.border}`,
                flexShrink: 0,
              }}
            />
            <Box component="span" sx={{ minWidth: 0, wordBreak: 'break-word' }}>
              {choice}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

function ResultHistoryCards({ results }) {
  if (!Array.isArray(results) || results.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mt: 1 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          mb: 0.5,
          color: 'rgba(15, 23, 42, 0.52)',
          fontSize: '11px',
          fontWeight: 800,
        }}
      >
        <Box component="span" sx={{ flexShrink: 0 }}>新しい</Box>
        <Box
          sx={{
            flex: 1,
            height: 1,
            bgcolor: 'rgba(15, 23, 42, 0.16)',
          }}
        />
        <Box component="span" sx={{ flexShrink: 0 }}>古い</Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 0.75,
        }}
      >
        {results.map((result, index) => {
          const colorSet = getAnswerColor(getResultIndex(result));
          const cardLabel = index === 0 ? '前々回' : `R${result.number}`;

          return (
            <Box
              key={result.number}
              title={getResultLabel(result)}
              sx={{
                minWidth: 44,
                height: 32,
                px: 0.75,
                borderRadius: '8px',
                bgcolor: colorSet.surface,
                border: `1px solid ${colorSet.primary}`,
                color: colorSet.primary,
                display: 'grid',
                placeItems: 'center',
                fontSize: '12px',
                fontWeight: 900,
                boxSizing: 'border-box',
              }}
            >
              {cardLabel}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

export function RecentRoundResultsSummary({
  results,
  targetNumbers,
  choices,
  maxResults,
  rangeLabel,
}) {
  const visibleResults = Array.isArray(results) ? results.slice(0, maxResults) : [];
  const previousResult = visibleResults[0];
  const historyResults = visibleResults.slice(1);
  const legendChoices = previousResult?.answers?.length ? previousResult.answers : choices;
  const emptyMessage = getPastResultEmptyMessage(targetNumbers);

  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: '16px',
        background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.04) 0%, rgba(15, 23, 42, 0.08) 100%)',
        border: '1px solid rgba(15, 23, 42, 0.10)',
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.65)',
        minHeight: 138,
        boxSizing: 'border-box',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          mb: 1,
          fontSize: '12px',
          color: 'rgba(15, 23, 42, 0.72)',
          fontWeight: 800,
        }}
      >
        <Box>最近の結果</Box>
        <Box>{rangeLabel}</Box>
      </Box>

      {visibleResults.length === 0 ? (
        <Box
          sx={{
            minHeight: 78,
            display: 'grid',
            placeItems: 'center',
            color: 'rgba(15, 23, 42, 0.56)',
            fontSize: '13px',
            fontWeight: 800,
            textAlign: 'center',
            border: '1px dashed rgba(15, 23, 42, 0.14)',
            borderRadius: '12px',
            px: 1,
          }}
        >
          {emptyMessage}
        </Box>
      ) : (
        <Box>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'baseline',
              gap: 0.75,
              fontSize: '14px',
              fontWeight: 900,
              color: 'rgba(15, 23, 42, 0.78)',
              lineHeight: 1.45,
            }}
          >
            <Box component="span">前回:</Box>
            <Box component="span" sx={{ color: getAnswerColor(getResultIndex(previousResult)).primary }}>
              {getResultLabel(previousResult, choices)}
            </Box>
          </Box>

          <ResultLegend choices={legendChoices} />
          <ResultHistoryCards results={historyResults} />
        </Box>
      )}
    </Box>
  );
}
